/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { InteractiveComponents } from '../../../../core/enums';
import { ScreenTaskControllerTypes } from '../../../../core/navigation';
import { setActiveInteractiveComponentAction } from '../../../interactivelayer/interactivelayer.actions';
import { clearActiveInteractiveComponent } from '../../../interactivelayer/interactiveLayer.reducers';
import { onMouseClickNoNetworkOverlay } from '../../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    hideOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../../overlaydialog/overlaydialog.reducers';
import { setCaretVisibilityAction, setVerticalPositionToDoListAction } from '../todolist.actions';
import {
    disableGhostModeToDoList,
    enableGhostModeToDoList,
    handleToDoListErrorOrNetwork,
    onKeyOkNoTaskToDoList,
    onLongPressOkToDoList,
} from '../todolist.reducers';
import { onKeyChannelDownToDoList, onKeyChannelUpToDoList } from './todolist.navigation.reducers';

const getHighlightPosition = (value: string | null | undefined): number | null => {
    if (!value) return null;

    const verticalStr = value.split('_')?.[1];
    if (!verticalStr) return null;

    const vertical = Number(verticalStr);

    if (Number.isNaN(vertical)) return null;

    return vertical;
};

export const onMouseClickToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { activeComponent } = getState().interactiveLayer;
    const { value } = getState().inputFactory;
    const { isError } = getState().todoList;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

            dispatch(defaultMouseClickTasks(activeComponent, value));
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
            dispatch(onMouseClickNoNetworkOverlay());
            break;
        case ScreenTaskControllerTypes.RECORD_CONFLICT_INFO_OVERLAY:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        default:
    }
};

export const onMouseOkLongPressToDoList = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const highlightPosition = getHighlightPosition(value);
    const { isError } = getState().todoList;

    if (handleToDoListErrorOrNetwork(dispatch, isError)) return;

    if (highlightPosition || highlightPosition === 0) {
        dispatch(onLongPressOkToDoList());
    }
};

export const onMouseOverToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(defaultMouseOverTasks());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_CONFLICT_INFO_OVERLAY:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        default:
    }
};

export const onMouseOutToDoList = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.NO_TASK:
            dispatch(defaultMouseOutTasks());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_CONFLICT_INFO_OVERLAY:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;

        default:
    }
};

const defaultMouseOverTasks = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    const { ghostMode } = getState().todoList;

    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setActiveInteractiveComponentAction(value));
            dispatch(setCaretVisibilityAction(true));
            dispatch(enableGhostModeToDoList());
            break;
        default:
    }

    const highlightPosition = getHighlightPosition(value);

    if (!highlightPosition && highlightPosition !== 0) {
        dispatch(enableGhostModeToDoList());
        return;
    }
    if (ghostMode) dispatch(disableGhostModeToDoList());

    dispatch(setVerticalPositionToDoListAction(highlightPosition));
};

const defaultMouseOutTasks = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    dispatch(enableGhostModeToDoList());
    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setCaretVisibilityAction(false));
            dispatch(clearActiveInteractiveComponent());
            break;
        default:
    }
};

const defaultMouseClickTasks =
    (activeInteractiveComponent: InteractiveComponents, inputFactoryValue: string) =>
    (dispatch: Function) => {
        if (activeInteractiveComponent === InteractiveComponents.SOUTH) {
            dispatch(onKeyChannelDownToDoList());
            return;
        }
        if (activeInteractiveComponent === InteractiveComponents.NORTH) {
            dispatch(onKeyChannelUpToDoList());
            return;
        }

        const highlightPosition = getHighlightPosition(inputFactoryValue);
        if (highlightPosition || highlightPosition === 0) {
            dispatch(onKeyOkNoTaskToDoList());
        }
    };
